// @ts-nocheck
import { useState } from 'react';
import { Stack, Title, Text as MantineText, Code, Group, SimpleGrid } from '@mantine/core';
import { Card, Button } from '../../../components/ui';
import { FadeIn, SlideIn, ScaleIn, RotateIn } from '../../../components/animations';

export default function TransitionsPage() {
  const [showFade, setShowFade] = useState(true);
  const [showSlide, setShowSlide] = useState(true);
  const [showScale, setShowScale] = useState(true);
  const [showRotate, setShowRotate] = useState(true);

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Transition Animations</Title>
        <MantineText c="dimmed" size="sm">
          Smooth entrance and exit animations for elements appearing and disappearing
        </MantineText>
      </Stack>

      {/* FadeIn */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>FadeIn</Title>
            <MantineText size="sm" c="dimmed">
              Fade elements in and out with configurable duration and delay
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Example:</MantineText>
            <Group>
              <Button onClick={() => setShowFade(!showFade)}>
                Toggle FadeIn
              </Button>
            </Group>
            <FadeIn duration={500} visible={showFade}>
              <Card variant="subtle" p="md">
                <MantineText>This card fades in smoothly</MantineText>
              </Card>
            </FadeIn>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<FadeIn duration={500}>
  <Card variant="subtle" p="md">
    <Text>This card fades in smoothly</Text>
  </Card>
</FadeIn>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>With Delay:</MantineText>
            <SimpleGrid cols={3} spacing="md">
              <FadeIn duration={500} delay={0}>
                <Card variant="outline" p="sm" ta="center">
                  <MantineText size="sm">No delay</MantineText>
                </Card>
              </FadeIn>
              <FadeIn duration={500} delay={200}>
                <Card variant="outline" p="sm" ta="center">
                  <MantineText size="sm">200ms delay</MantineText>
                </Card>
              </FadeIn>
              <FadeIn duration={500} delay={400}>
                <Card variant="outline" p="sm" ta="center">
                  <MantineText size="sm">400ms delay</MantineText>
                </Card>
              </FadeIn>
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<FadeIn duration={500} delay={0}>
  <Card>No delay</Card>
</FadeIn>
<FadeIn duration={500} delay={200}>
  <Card>200ms delay</Card>
</FadeIn>
<FadeIn duration={500} delay={400}>
  <Card>400ms delay</Card>
</FadeIn>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Key Props:</MantineText>
            <MantineText size="xs" c="dimmed" component="div">
              • duration - Animation duration in milliseconds (default: 300)
              <br />
              • delay - Delay before animation starts (default: 0)
              <br />
              • easing - CSS easing function (default: 'ease-out')
            </MantineText>
          </Stack>
        </Stack>
      </Card>

      {/* SlideIn */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>SlideIn</Title>
            <MantineText size="sm" c="dimmed">
              Slide elements in from different directions
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Example:</MantineText>
            <Group>
              <Button onClick={() => setShowSlide(!showSlide)}>
                Toggle SlideIn
              </Button>
            </Group>
            <SimpleGrid cols={2} spacing="md">
              <>
                <SlideIn direction="left" duration={500} visible={showSlide}>
                  <Card variant="subtle" p="md" ta="center">
                    <MantineText size="sm">From Left</MantineText>
                  </Card>
                </SlideIn>
                <SlideIn direction="right" duration={500} visible={showSlide}>
                  <Card variant="subtle" p="md" ta="center">
                    <MantineText size="sm">From Right</MantineText>
                  </Card>
                </SlideIn>
                <SlideIn direction="top" duration={500} visible={showSlide}>
                  <Card variant="subtle" p="md" ta="center">
                    <MantineText size="sm">From Top</MantineText>
                  </Card>
                </SlideIn>
                <SlideIn direction="bottom" duration={500} visible={showSlide}>
                  <Card variant="subtle" p="md" ta="center">
                    <MantineText size="sm">From Bottom</MantineText>
                  </Card>
                </SlideIn>
              </>
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<SlideIn direction="left" duration={500}>
  <Card>From Left</Card>
</SlideIn>
<SlideIn direction="right" duration={500}>
  <Card>From Right</Card>
</SlideIn>
<SlideIn direction="top" duration={500}>
  <Card>From Top</Card>
</SlideIn>
<SlideIn direction="bottom" duration={500}>
  <Card>From Bottom</Card>
</SlideIn>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Key Props:</MantineText>
            <MantineText size="xs" c="dimmed" component="div">
              • direction - 'left' | 'right' | 'top' | 'bottom' (default: 'left')
              <br />
              • distance - Slide distance in pixels (default: 50)
              <br />
              • duration - Animation duration in ms (default: 300)
              <br />
              • delay - Delay before animation starts
            </MantineText>
          </Stack>
        </Stack>
      </Card>

      {/* ScaleIn */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>ScaleIn</Title>
            <MantineText size="sm" c="dimmed">
              Scale elements from small to full size
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Example:</MantineText>
            <Group>
              <Button onClick={() => setShowScale(!showScale)}>
                Toggle ScaleIn
              </Button>
            </Group>
            <ScaleIn duration={500} initialScale={0.5} visible={showScale}>
              <Card variant="gradient" gradientVariant="primary" p="md" ta="center">
                <MantineText c="white" fw={600}>
                  This card scales in from 50%
                </MantineText>
              </Card>
            </ScaleIn>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<ScaleIn duration={500} initialScale={0.5}>
  <Card variant="gradient" gradientVariant="primary">
    This card scales in from 50%
  </Card>
</ScaleIn>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Key Props:</MantineText>
            <MantineText size="xs" c="dimmed" component="div">
              • initialScale - Starting scale (default: 0.8)
              <br />
              • duration - Animation duration in ms (default: 300)
              <br />
              • delay - Delay before animation starts
            </MantineText>
          </Stack>
        </Stack>
      </Card>

      {/* RotateIn */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>RotateIn</Title>
            <MantineText size="sm" c="dimmed">
              Rotate elements as they appear
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Example:</MantineText>
            <Group>
              <Button onClick={() => setShowRotate(!showRotate)}>
                Toggle RotateIn
              </Button>
            </Group>
            <RotateIn duration={500} degrees={-180} visible={showRotate}>
              <Card variant="subtle" p="md" ta="center">
                <MantineText fw={600}>This card rotates in</MantineText>
              </Card>
            </RotateIn>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<RotateIn duration={500} degrees={-180}>
  <Card variant="subtle">
    This card rotates in
  </Card>
</RotateIn>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Key Props:</MantineText>
            <MantineText size="xs" c="dimmed" component="div">
              • degrees - Rotation degrees (default: -360)
              <br />
              • duration - Animation duration in ms (default: 500)
              <br />
              • delay - Delay before animation starts
            </MantineText>
          </Stack>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap="xs">
              <MantineText size="sm" fw={500}>Modal/Dialog Entrances</MantineText>
              <MantineText size="xs" c="dimmed">
                Use ScaleIn or FadeIn for smooth modal appearances. Combine with SlideIn for side drawers.
              </MantineText>
            </Stack>

            <Stack gap="xs">
              <MantineText size="sm" fw={500}>List Item Animations</MantineText>
              <MantineText size="xs" c="dimmed">
                Stagger FadeIn or SlideIn animations with delays for sequential list reveals.
              </MantineText>
            </Stack>

            <Stack gap="xs">
              <MantineText size="sm" fw={500}>Page Transitions</MantineText>
              <MantineText size="xs" c="dimmed">
                Apply SlideIn to page content for smooth navigation between views.
              </MantineText>
            </Stack>

            <Stack gap="xs">
              <MantineText size="sm" fw={500}>Notification Toasts</MantineText>
              <MantineText size="xs" c="dimmed">
                Combine SlideIn from top/bottom with FadeIn for attention-grabbing notifications.
              </MantineText>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
